/**
 * SoleadLab 2.0 - Storage Admin Modal
 *
 * Admin-only modal for scanning and cleaning orphaned R2 assets.
 * Opens via the admin (shield) button in the left rail.
 */

import { useState, useCallback } from 'react';
import { useUIStore } from '../../stores';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { apiFetch, API_BASE } from '../../services/apiFetch';
import './StorageAdminModal.css';

interface OrphanedAsset {
  key: string;
  size: number;
  originalName?: string;
  contentType?: string;
}

interface ScanResult {
  scanned: number;
  referenced: number;
  orphanedCount: number;
  orphanedSizeBytes: number;
  orphanedSizeMB: number;
  truncated: boolean;
  siteNames: Record<string, string>;
  orphanedKeys: OrphanedAsset[];
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val < 10 ? val.toFixed(2) : val < 100 ? val.toFixed(1) : Math.round(val)} ${units[i]}`;
}

/** Category label from R2 key path */
function categoryLabel(key: string): string {
  const parts = key.split('/');
  // sites/{siteId}/{category}/... or uploads/{siteId}/...
  if (parts[0] === 'uploads') return 'Mobile Upload';
  const cat = parts[2] || 'other';
  const labels: Record<string, string> = {
    photo: 'Photo', overlay: 'Overlay/PDF', orthophoto: 'Orthophoto',
    sld: 'SLD Diagram', videos: 'Video', 'geo_photo': 'Geo Photo',
    general: 'General',
  };
  return labels[cat] || cat;
}

/** File type icon based on content type or extension */
function fileTypeTag(asset: OrphanedAsset): string {
  const ct = asset.contentType || '';
  if (ct.startsWith('image/')) return 'IMG';
  if (ct.startsWith('video/')) return 'VID';
  if (ct === 'application/pdf') return 'PDF';
  if (ct === 'application/json') return 'JSON';
  const ext = asset.key.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'webp', 'tiff', 'tif'].includes(ext)) return 'IMG';
  if (['mp4', 'mov', 'avi'].includes(ext)) return 'VID';
  if (ext === 'pdf') return 'PDF';
  return ext.toUpperCase() || 'FILE';
}

/** Group assets by site, with site name and category breakdown */
function groupBySite(
  keys: OrphanedAsset[],
  siteNames: Record<string, string>
): Array<{
  siteId: string;
  siteName: string;
  prefix: string;
  assets: OrphanedAsset[];
  totalSize: number;
}> {
  const map = new Map<string, OrphanedAsset[]>();
  for (const asset of keys) {
    const parts = asset.key.split('/');
    const prefix = parts.length >= 2 ? `${parts[0]}/${parts[1]}` : 'other';
    (map.get(prefix) ?? (map.set(prefix, []), map.get(prefix)!)).push(asset);
  }

  return Array.from(map.entries()).map(([prefix, assets]) => {
    const siteId = prefix.split('/')[1] || '';
    return {
      siteId,
      siteName: siteNames[siteId] || '',
      prefix,
      assets,
      totalSize: assets.reduce((s, a) => s + a.size, 0),
    };
  });
}

export default function StorageAdminModal() {
  const closeModal = useUIStore((s) => s.closeModal);
  const addNotification = useUIStore((s) => s.addNotification);

  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  // ── Scan ──────────────────────────────────────────────────────────────────
  const handleScan = useCallback(async () => {
    setScanning(true);
    setError('');
    setScanResult(null);
    setSelected(new Set());
    try {
      const data = await apiFetch<ScanResult>('/api/v2/admin/orphaned-assets');
      setScanResult(data);
      setSelected(new Set(data.orphanedKeys.map((o) => o.key)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  }, []);

  // ── Delete selected ────────────────────────────────────────────────────────
  const handleDelete = useCallback(async () => {
    if (selected.size === 0) return;
    const keys = Array.from(selected);
    if (
      !window.confirm(
        `Delete ${keys.length} orphaned asset${keys.length > 1 ? 's' : ''}?\n\nThis cannot be undone.`
      )
    )
      return;

    setDeleting(true);
    setError('');
    try {
      let totalDeleted = 0;
      const totalFailed: string[] = [];

      for (let i = 0; i < keys.length; i += 500) {
        const batch = keys.slice(i, i + 500);
        const data = await apiFetch<{ deleted: number; failed: string[]; total: number }>(
          '/api/v2/admin/orphaned-assets',
          {
            method: 'DELETE',
            body: JSON.stringify({ keys: batch }),
          },
        );
        totalDeleted += data.deleted;
        totalFailed.push(...data.failed);
      }

      addNotification({
        type: totalFailed.length > 0 ? 'warning' : 'success',
        title: `Deleted ${totalDeleted} asset${totalDeleted !== 1 ? 's' : ''}`,
        message: totalFailed.length > 0
          ? `${totalFailed.length} failed to delete`
          : undefined,
      });

      if (scanResult) {
        const deletedSet = new Set(keys.filter((k) => !totalFailed.includes(k)));
        const remaining = scanResult.orphanedKeys.filter(
          (o) => !deletedSet.has(o.key)
        );
        const remainingSize = remaining.reduce((s, o) => s + o.size, 0);
        setScanResult({
          ...scanResult,
          orphanedCount: remaining.length,
          orphanedSizeBytes: remainingSize,
          orphanedSizeMB:
            Math.round((remainingSize / 1024 / 1024) * 100) / 100,
          orphanedKeys: remaining,
        });
        setSelected(new Set(totalFailed));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [selected, scanResult, addNotification]);

  // ── Toggle selection ───────────────────────────────────────────────────────
  const toggleKey = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleGroup = (assets: OrphanedAsset[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = assets.every((a) => next.has(a.key));
      for (const a of assets) {
        if (allSelected) next.delete(a.key);
        else next.add(a.key);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (!scanResult) return;
    if (selected.size === scanResult.orphanedKeys.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(scanResult.orphanedKeys.map((o) => o.key)));
    }
  };

  const selectedSize = scanResult
    ? scanResult.orphanedKeys
        .filter((o) => selected.has(o.key))
        .reduce((s, o) => s + o.size, 0)
    : 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  const groups = scanResult
    ? groupBySite(scanResult.orphanedKeys, scanResult.siteNames)
    : [];

  return (
    <Modal
      isOpen={true}
      onClose={closeModal}
      title="Storage Admin"
      size="lg"
      className="storage-admin-modal"
    >
      <div className="sa-content">
        {/* ── Header actions ── */}
        <div className="sa-actions">
          <Button
            variant="primary"
            onClick={handleScan}
            loading={scanning}
            disabled={deleting}
          >
            {scanResult ? 'Re-scan' : 'Scan for Orphans'}
          </Button>

          {scanResult && scanResult.orphanedCount > 0 && (
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleting}
              disabled={scanning || selected.size === 0}
            >
              Delete Selected ({selected.size})
            </Button>
          )}
        </div>

        {error && <div className="sa-error">{error}</div>}

        {/* ── Scan summary ── */}
        {scanResult && (
          <div className="sa-summary">
            <div className="sa-stat">
              <span className="sa-stat__value">{scanResult.scanned}</span>
              <span className="sa-stat__label">R2 Objects</span>
            </div>
            <div className="sa-stat">
              <span className="sa-stat__value">{scanResult.referenced}</span>
              <span className="sa-stat__label">Referenced</span>
            </div>
            <div className="sa-stat sa-stat--orphaned">
              <span className="sa-stat__value">{scanResult.orphanedCount}</span>
              <span className="sa-stat__label">Orphaned</span>
            </div>
            <div className="sa-stat sa-stat--size">
              <span className="sa-stat__value">
                {formatBytes(scanResult.orphanedSizeBytes)}
              </span>
              <span className="sa-stat__label">Orphan Size</span>
            </div>
            {scanResult.truncated && (
              <div className="sa-truncated">
                Scan was truncated. Increase limit to scan more objects.
              </div>
            )}
          </div>
        )}

        {/* ── Empty state ── */}
        {scanResult && scanResult.orphanedCount === 0 && (
          <div className="sa-empty">
            No orphaned assets found. Storage is clean.
          </div>
        )}

        {/* ── Orphaned assets list ── */}
        {scanResult && scanResult.orphanedCount > 0 && (
          <div className="sa-list">
            <div className="sa-list__header">
              <label className="sa-checkbox">
                <input
                  type="checkbox"
                  checked={selected.size === scanResult.orphanedKeys.length}
                  onChange={toggleAll}
                />
                Select All
              </label>
              {selected.size > 0 && (
                <span className="sa-list__selected-size">
                  {selected.size} selected &middot; {formatBytes(selectedSize)}
                </span>
              )}
            </div>

            <div className="sa-groups">
              {groups.map(({ siteId, siteName, prefix, assets, totalSize }) => {
                const groupAllSelected = assets.every((a) => selected.has(a.key));
                const deletedSite = !siteName && siteId;
                return (
                  <div key={prefix} className="sa-group">
                    <div className="sa-group__header">
                      <label className="sa-checkbox">
                        <input
                          type="checkbox"
                          checked={groupAllSelected}
                          onChange={() => toggleGroup(assets)}
                        />
                        <span className="sa-group__name">
                          {siteName || prefix}
                        </span>
                      </label>
                      <div className="sa-group__meta">
                        {deletedSite && (
                          <span className="sa-tag sa-tag--deleted">Deleted Site</span>
                        )}
                        <span className="sa-group__count">
                          {assets.length} file{assets.length !== 1 ? 's' : ''} &middot;{' '}
                          {formatBytes(totalSize)}
                        </span>
                      </div>
                    </div>
                    <div className="sa-group__items">
                      {assets.map((asset) => {
                        const displayName =
                          asset.originalName ||
                          asset.key.split('/').pop() ||
                          asset.key;
                        const category = categoryLabel(asset.key);
                        const tag = fileTypeTag(asset);
                        const previewUrl = `${API_BASE}/api/v2/assets/${asset.key}`;
                        return (
                          <div key={asset.key} className="sa-item">
                            <input
                              type="checkbox"
                              checked={selected.has(asset.key)}
                              onChange={() => toggleKey(asset.key)}
                            />
                            <span className={`sa-tag sa-tag--${tag.toLowerCase()}`}>
                              {tag}
                            </span>
                            <div className="sa-item__info">
                              <a
                                className="sa-item__name"
                                href={previewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`${asset.key}\n\nClick to preview`}
                              >
                                {displayName}
                              </a>
                              <span className="sa-item__category">{category}</span>
                            </div>
                            <span className="sa-item__size">
                              {formatBytes(asset.size)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Idle state ── */}
        {!scanResult && !scanning && (
          <div className="sa-idle">
            <p>
              Scan your R2 storage bucket to find assets that are no longer referenced
              by any project. Orphaned files can be safely deleted to free up space.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
